/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import { Button, Checkbox, Hidden, PopoverOrigin, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
    ArrowDownIcon,
    CheckBoxCheckIcon,
    CheckBoxNoCheckIcon,
    RadioBoxCheckIcon,
    RadioBoxNoCheckIcon,
} from "@/assets/icons";
import { SearchField } from "@/components/atoms";
import { useGlobalFilter, usePolicyholders } from "@/shared/hooks";

import {
    CleanFilters,
    CloseIconMobile,
    Container,
    DropdownContainer,
    FilterButton,
    FilterOptionLabel,
    FilterOptions,
    LineSeparator,
    MobilePageTitle,
    RowContainer,
    StyledFormControlLabel,
    StyledPopover,
} from "./styled";
import { mapPolicyholderDropdownOption, MAX_POLICYHOLDERS } from "./utils";

type PolicyholderDropdownProps = {
    title: string;
};

const PolicyholderDropdown: FC<PolicyholderDropdownProps> = ({ title }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { policyholdersGF, setPolicyholdersGF, setIsProEnvironment, isProEnvironment } = useGlobalFilter();
    const { getPolicyholders } = usePolicyholders();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
    const [filterSearch, setFilterSearch] = useState("");

    const policyholders = getPolicyholders();
    const filters = mapPolicyholderDropdownOption(policyholders);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFilterSelection = (filterId: number) => {
        if (isProEnvironment) {
            setPolicyholdersGF([filterId]);
        } else {
            const index = policyholdersGF.indexOf(filterId, 0);
            if (index > -1) {
                const arrayCopy = [...policyholdersGF];
                arrayCopy.splice(index, 1);
                setPolicyholdersGF(arrayCopy);
            } else {
                setPolicyholdersGF([...policyholdersGF, filterId]);
            }
        }
    };

    const anchorOrigin: PopoverOrigin = !isMobileScreen
        ? {
              vertical: "bottom",
              horizontal: "left",
          }
        : { vertical: "bottom", horizontal: "left" };

    const transformOrigin: PopoverOrigin = !isMobileScreen
        ? {
              vertical: "top",
              horizontal: "left",
          }
        : { vertical: "top", horizontal: "left" };

    return (
        <div>
            <Container>
                <RowContainer>
                    <DropdownContainer>
                        <FilterButton
                            disableRipple
                            $active={policyholdersGF.length > 0}
                            onClick={handleClick}
                            data-testid="filter_dropdown-button-dropdown"
                            endIcon={<ArrowDownIcon color="secondary" />}
                        >
                            {t("PolicyHolders.PolicyHolders")}
                        </FilterButton>
                    </DropdownContainer>
                </RowContainer>
            </Container>
            <StyledPopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                classes={{ paper: "popover-paper" }}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            >
                <FilterOptions>
                    <Hidden smUp>
                        <div className="d-flex align-center justify-space-between mb10">
                            <div className="d-flex align-center">
                                <CloseIconMobile onClick={handleClose} color="secondary" />
                                <MobilePageTitle className="ml10">{title}</MobilePageTitle>
                            </div>

                            <CleanFilters
                                data-testid="filter_dropdown-button-clear"
                                onClick={() => setIsProEnvironment(isProEnvironment)}
                            >
                                {t("Common.Filters.Clear")}
                            </CleanFilters>
                        </div>
                        <LineSeparator />
                    </Hidden>

                    {((isMobileScreen && filters.length > 10) || (!isMobileScreen && filters.length > 6)) && (
                        <>
                            <SearchField placeholder={t("Common.SearchDots")} onSearch={setFilterSearch} />
                            <LineSeparator />
                        </>
                    )}
                    {filters
                        .filter((f) => f.name.toLocaleLowerCase().includes(filterSearch.toLocaleLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((filter) => {
                            const onlyOneSelected = policyholdersGF.length === 1 && policyholdersGF.includes(filter.id);
                            const notSelectedMaxReached =
                                policyholdersGF.length >= MAX_POLICYHOLDERS && !policyholdersGF.includes(filter.id);
                            return (
                                <StyledFormControlLabel
                                    data-testid={`filter_dropdown-option-${filter.id}`}
                                    key={`filter_dropdown-option-${filter.id}`}
                                    $selectionDisabled={onlyOneSelected}
                                    $unselectionDisabled={notSelectedMaxReached}
                                    control={
                                        <Checkbox
                                            icon={isProEnvironment ? <RadioBoxNoCheckIcon /> : <CheckBoxNoCheckIcon />}
                                            checkedIcon={
                                                isProEnvironment ? <RadioBoxCheckIcon /> : <CheckBoxCheckIcon />
                                            }
                                            checked={policyholdersGF.includes(filter.id)}
                                            disabled={onlyOneSelected || notSelectedMaxReached}
                                            onChange={() => handleFilterSelection(filter.id)}
                                            inputProps={{
                                                // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                                // @ts-ignore
                                                "data-testid": `checkbox-input-option-${filter.id}`,
                                            }}
                                        />
                                    }
                                    label={
                                        <FilterOptionLabel className="filterOptionLabel">
                                            {filter.name}
                                        </FilterOptionLabel>
                                    }
                                />
                            );
                        })}
                </FilterOptions>

                <Hidden smUp>
                    <Button className="mt20" fullWidth data-testid="button_confirm" onClick={handleClose}>
                        {t("Common.Confirm")}
                    </Button>
                </Hidden>
            </StyledPopover>
        </div>
    );
};

export default PolicyholderDropdown;
